import { useState } from 'react';
import { AlertTriangle, FileDown, Power, Zap } from 'lucide-react';
import type { RobotState } from '@fabricos/protocol-ts';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { sendCommand } from '@/hooks/useHubSocket';
import { useFleet } from '@/store/fleet';
import { cn } from '@/lib/utils';

/**
 * Live action cards: Reboot, Motor test, sensor calibrations. The Hub's
 * pre-flight guard refuses any of these while the vehicle is armed; the
 * frontend additionally disables the relevant buttons + shows the reason.
 *
 * Param backup/restore and DataFlash log download are still stubbed —
 * they need their own protocol surface (multi-frame LOG_REQUEST_DATA
 * with chunk progress + the param-cache → file plumbing).
 */
export function ActionCards({ robotId }: { robotId: string }) {
  const state = useFleet((s) => s.states[robotId]);
  const isArmed = state?.arm === 'armed';
  return (
    <div className="space-y-3">
      <ArmedBanner armed={isArmed} />
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <RebootCard robotId={robotId} armed={isArmed} />
        <MotorTestCard robotId={robotId} armed={isArmed} />
        {/* Calibration flows moved to the Calibrate tab (CalibrationCenter)
            — guided wizards with live progress instead of bare start/cancel. */}
        <Card
          icon={FileDown}
          title="Param backup / restore"
          tone="default"
          blurb='Snapshot the full param table to a JSON sidecar; restore from one. Pending — once the param browser pages through all ~1368 entries, "Export" wires up trivially.'
          adr="needs spec — diff display + per-restart-required filtering"
          button={<Button size="sm" disabled>Export</Button>}
        />
        <Card
          icon={FileDown}
          title="DataFlash log download"
          tone="default"
          blurb="LOG_REQUEST_LIST → LOG_REQUEST_DATA. Multi-frame chunked protocol with cancel/resume. Larger slice than fit in this pass."
          adr="needs spec — chunked progress UI + cancellation"
          button={<Button size="sm" disabled>List logs</Button>}
        />
      </div>
    </div>
  );
}

function ArmedBanner({ armed }: { armed: boolean }) {
  if (!armed) return null;
  return (
    <div className="flex items-center gap-2 rounded border border-status-critical/40 bg-status-critical/10 px-3 py-2 text-[12px] text-status-critical">
      <AlertTriangle className="h-4 w-4" />
      <span className="font-medium">Vehicle armed — configuration actions are refused.</span>
      <span className="text-fg-secondary">Disarm before running motor test, calibration, or reboot.</span>
    </div>
  );
}

function RebootCard({ robotId, armed }: { robotId: string; armed: boolean }) {
  const [confirming, setConfirming] = useState(false);
  function go() {
    sendCommand({
      kind: 'reboot',
      id: `reboot-${Date.now()}`,
      robot_id: robotId,
      priority: 'normal',
    });
    setConfirming(false);
  }
  return (
    <Card
      icon={Power}
      title="Reboot autopilot"
      tone="critical"
      blurb="Sends MAV_CMD_PREFLIGHT_REBOOT_SHUTDOWN. Required after writing boot-time params (FRAME_CLASS, MOT_PWM_*, BATT_MONITOR). Link drops for 5–10 s."
      adr={confirming ? 'click "Confirm reboot" to send' : 'press to confirm'}
      button={
        confirming ? (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
            <Button size="sm" onClick={go} disabled={armed}>Confirm reboot</Button>
          </div>
        ) : (
          <Button size="sm" disabled={armed} onClick={() => setConfirming(true)}>
            Reboot…
          </Button>
        )
      }
    />
  );
}

function MotorTestCard({ robotId, armed }: { robotId: string; armed: boolean }) {
  const [motor, setMotor] = useState('1');
  const [throttle, setThrottle] = useState('10');
  const [duration, setDuration] = useState('2');
  function go() {
    sendCommand({
      kind: 'motor_test',
      id: `motor-test-${Date.now()}`,
      robot_id: robotId,
      priority: 'normal',
      motor_index: Math.max(1, Math.min(8, Number(motor) || 1)),
      throttle_pct: Math.max(1, Math.min(15, Number(throttle) || 10)),
      duration_s: Math.max(1, Math.min(10, Number(duration) || 2)),
    });
  }
  return (
    <div
      className={cn(
        'rounded border bg-surface-1 p-3',
        armed ? 'border-border-default opacity-60' : 'border-status-warn/40',
      )}
    >
      <div className="flex items-start gap-2.5">
        <Zap className="h-4 w-4 flex-none text-status-warn" />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-fg-primary">Motor test</div>
          <p className="mt-0.5 text-[11px] leading-relaxed text-fg-secondary">
            Spin one motor at low throttle. Hub clamps throttle to 15% and duration to 10 s regardless of input.
            <strong className="ml-1 text-status-critical">Remove props before bench testing.</strong>
          </p>
          <div className="mt-2 grid grid-cols-3 gap-1.5 font-mono text-[10px]">
            <Field label="motor (1..8)">
              <Input
                value={motor}
                onChange={(e) => setMotor(e.target.value)}
                className="h-7 text-[11px]"
                disabled={armed}
              />
            </Field>
            <Field label="throttle %">
              <Input
                value={throttle}
                onChange={(e) => setThrottle(e.target.value)}
                className="h-7 text-[11px]"
                disabled={armed}
              />
            </Field>
            <Field label="seconds">
              <Input
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="h-7 text-[11px]"
                disabled={armed}
              />
            </Field>
          </div>
        </div>
        <Button size="sm" onClick={go} disabled={armed} title={armed ? 'Disarm first' : 'Spin selected motor'}>
          Spin
        </Button>
      </div>
    </div>
  );
}

function Card({
  title,
  icon: Icon,
  tone,
  blurb,
  adr,
  button,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: 'default' | 'warn' | 'critical';
  blurb: string;
  adr: string;
  button: React.ReactNode;
}) {
  return (
    <div className="rounded border border-border-default bg-surface-1 p-3">
      <div className="flex items-start gap-2.5">
        <Icon
          className={cn(
            'h-4 w-4 flex-none',
            tone === 'critical' && 'text-status-critical',
            tone === 'warn' && 'text-status-warn',
            tone === 'default' && 'text-fg-secondary',
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-fg-primary">{title}</div>
          <p className="mt-0.5 text-[11px] leading-relaxed text-fg-secondary">{blurb}</p>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-muted">
            {adr}
          </div>
        </div>
        {button}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-0.5 text-fg-muted">{label}</div>
      {children}
    </div>
  );
}

// Hint helper not used elsewhere but kept for future tweaks.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _UnusedRobotState(_: RobotState) {}
